/**
 * ExportButton — the ONE way a list page offers a download.
 *
 * WHY THIS EXISTS
 * ---------------
 * Audited 2026-08-09 after the owner asked why `/organization/positions` had no
 * export. It had none, and the four pages that did had hand-rolled a browser CSV
 * each — while `utils/exportTable.ts` already held the correct implementation and
 * was used by exactly ONE page. Each copy was wrong in a different way:
 *
 *   Departments   exported `paginatedDepartments` — the CURRENT PAGE ONLY, and said
 *                 so in a comment, so it shipped known-incomplete
 *   ActivityLogs  built rows with `.join(',')` and NO escaping, so any value
 *                 containing a comma silently corrupted the file
 *   all four      omitted the UTF-8 BOM that `exportTable` writes with the comment
 *                 "so Excel opens non-ASCII rows correctly" — on a product shipping
 *                 zh-CN, ja, ko and ar, that is a garbled file for every non-ASCII
 *                 name
 *
 * That is `svdy-rules` §10.5 exactly: the knowledge lived in one place and the
 * copies did not have it. A shared BUTTON rather than only a shared function is
 * what makes it enforceable — a page cannot half-adopt it, and
 * `src/i18n/exportSurface.test.ts` fails on any new hand-rolled download.
 *
 * WHY CSV + EXCEL, AND WHY NOT PDF
 * --------------------------------
 * The market answer for this category, checked rather than assumed: Deputy exports
 * timesheets as "CSV/EXCEL - All fields" and its Data Exporter produces CSV;
 * Homebase downloads time cards as CSV/Excel and tells you to PRINT if you want a
 * PDF. So PDF is not a data-export format here, it is the print path — and a PDF of
 * a departments list serves nobody.
 *
 * `AttendanceReports` keeps its PDF (`allowPdf`) because there the artefact IS a
 * document: a manager files or hands over a signed timesheet in a pay dispute. That
 * is the one surface where the reader is a person rather than another system.
 *
 * WHAT THE CALLER OWES
 * --------------------
 * `rows` must be the COMPLETE filtered set, never the paginated slice. The reason is
 * not tidiness: FLSA requires time records for two years and payroll records for
 * three (California, four), so an export is evidence, and a file holding page 1 of 4
 * is worse than no file because it looks complete.
 *
 * Column labels are the CALLER's choice, deliberately:
 *   - a records / payroll export localises them, because a human reads it in Excel
 *   - a ROUND-TRIP export does not use this component at all — it downloads the
 *     import template, whose header must stay the importer's literal English bytes
 *     (ORG_IMPORT_DESIGN §11.3.1)
 */

import React, { useState } from 'react';
import { Button, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  FileDownloadOutlined,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { exportTable, type ExportFormat } from '../../utils/exportTable';

export interface ExportButtonProps {
  /** File basename, no extension. */
  filename: string;
  /** Shown at the top of a PDF and as the Excel sheet name. */
  title: string;
  /** Column labels, in order. Localised for records exports. */
  columns: string[];
  /**
   * The COMPLETE filtered set, keyed by the same labels as `columns`.
   * Passing a paginated slice is the defect this component was built to end.
   */
  rows: Record<string, unknown>[];
  /** Reports only — see the note above on why PDF is not offered by default. */
  allowPdf?: boolean;
  /** Surfaced to the page so it can show its own snackbar. */
  onDone?: (format: ExportFormat) => void;
  onError?: (message: string) => void;
  disabled?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  filename,
  title,
  columns,
  rows,
  allowPdf = false,
  onDone,
  onError,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const empty = rows.length === 0;
  // Disabled rather than a toast saying "nothing to export". The button already
  // knows, so telling the user after they commit to a click is worse — and it was
  // five copies of the same string across five pages.
  const isDisabled = disabled || empty;

  const formats: ExportFormat[] = allowPdf
    ? ['CSV', 'Excel', 'PDF']
    : ['CSV', 'Excel'];

  const run = async (format: ExportFormat) => {
    setAnchor(null);
    try {
      await exportTable(format, { filename, title, columns, rows });
      onDone?.(format);
    } catch (e) {
      onError?.(e instanceof Error ? e.message : t('common:export.failed'));
    }
  };

  const button = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<FileDownloadOutlined />}
      endIcon={<KeyboardArrowDown />}
      disabled={isDisabled}
      onClick={(e) => setAnchor(e.currentTarget)}
      sx={{
        borderRadius: 1,
        textTransform: 'none',
        fontSize: '0.875rem',
        color: 'text.primary',
        borderColor: 'rgba(0,0,0,0.2)',
        bgcolor: '#fff',
        '&:hover': { borderColor: 'rgba(0,0,0,0.35)' },
      }}
    >
      {t('common:export.button')}
    </Button>
  );

  return (
    <>
      {empty ? (
        // A disabled MUI button swallows pointer events, so the tooltip needs a
        // wrapper element to hang on or it never appears.
        <Tooltip title={t('common:export.nothingToExport')}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {formats.map((f) => (
          <MenuItem key={f} onClick={() => run(f)}>
            {t(`common:export.format.${f.toLowerCase()}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ExportButton;
